import * as React from "react";
import styled from "styled-components";
import { select, useSelect } from "@hubper/theme";
import { PoseGroup } from "react-pose";
import { animated, useSpring } from "react-spring";
import mergeStyles from "../../util/mergeStyles";
import { useOutsideClick } from "../../hooks";

import Text from "../../elements/Text";
import Avatar from "../../elements/Avatar";
import MenuLayer from "../../components/MenuLayer";
import Menu from "./Menu";
import Item from "./Item";
import LanguageSelect from "./LanguageSelect";

const HEIGHT = 42;

const Base = styled(animated.div)<{ open: boolean }>`
  transition: box-shadow 0.1s ease-in-out;
  box-sizing: border-box;
  height: ${HEIGHT}px;
  display: inline-flex;
  padding: 0px ${select.space(1)};
  border-radius: ${select.radius("normal")};
  border-color: ${select.color(["grey", 1])};
  border-style: solid;
  border-width: 1px;
  align-items: center;
  cursor: pointer;
  user-select: none;

  box-shadow: 0 1px 6px rgba(0, 0, 0, ${p => (p.open ? 0.1 : 0)});

  &:hover {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
  }
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  name: string;
  avatarSrc: string;
  isLoading?: boolean;
  children?: any;
};

function AccountMenu({
  style,
  className,
  name,
  avatarSrc,
  isLoading,
  children
}: Props) {
  const { space, color, radius } = useSelect();

  const baseRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = React.useState(false);

  const baseStyle = useSpring({
    borderBottomLeftRadius: isOpen ? "0px" : radius("normal"),
    borderBottomRightRadius: isOpen ? "0px" : radius("normal")
  });

  useOutsideClick([baseRef, menuRef], () => {
    if (isOpen) {
      setOpen(false);
    }
  });

  return (
    <>
      <Base
        ref={baseRef}
        style={mergeStyles(style, baseStyle)}
        className={className}
        onClick={() => {
          if (!isLoading) {
            setOpen(!isOpen);
          }
        }}
        open={isOpen}
      >
        <Text
          c={["grey", 7]}
          style={{ marginLeft: space(4), marginRight: space(3) }}
          fs={-1}
        >
          {isLoading ? (
            <div
              style={{
                height: "1em",
                width: 96,
                backgroundColor: color(["grey", 0])
              }}
            />
          ) : (
            name
          )}
        </Text>
        <Avatar size={32} src={avatarSrc} isLoading={isLoading} />
      </Base>
      <MenuLayer element={baseRef.current}>
        {style => {
          return (
            <PoseGroup flipMove={false}>
              {isOpen && (
                <Menu
                  ref={menuRef}
                  key="menu"
                  style={{ ...style, top: (style as any).top - 2 }}
                >
                  {React.Children.map(children, (child, index) =>
                    child
                      ? React.cloneElement(child, {
                          key: index,
                          onClick: (...args) => {
                            if (child.props.closeOnClick) {
                              setOpen(false);
                            }

                            if (child.props.onClick) {
                              child.props.onClick(...args);
                            }
                          }
                        })
                      : null
                  )}
                </Menu>
              )}
            </PoseGroup>
          );
        }}
      </MenuLayer>
    </>
  );
}

AccountMenu.Item = Item;
AccountMenu.LanguageSelect = LanguageSelect;

export default AccountMenu;
